import { useState, useEffect, useMemo } from 'react';
import {
  Select, MultiSelect, SegmentedControl, Group, Button, Table, Loader, Center, Text, Modal, Stack,
  TextInput, NumberInput, Badge, ActionIcon, Tooltip,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconPencil, IconTrash, IconSearch, IconChecklist } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { useClasses, useSubjects } from '../hooks/useLookups';
import { EmptyState } from '../components/EmptyState';
import { ExamHistory } from '../components/ExamHistory';
import { FetchOverlay } from '../components/FetchOverlay';
import { PageHeader } from '../components/PageHeader';
import { TableSkeleton } from '../components/TableSkeleton';
import { TableToolbar } from '../components/TableToolbar';
import { SectionCard } from '../components/SectionCard';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete } from '../lib/confirm';
import { formatDate } from '../lib/format';

/** Imtihonlarni yaratish/tahrirlash/o'chirish faqat shu rollarga (backend `managerOnly` bilan bir xil). */
const MANAGER_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'ACCOUNTANT', 'RECEPTION'];
/** `GET /staff` faqat shu rollarga ochiq — o'qituvchi so'rasa 403 oladi (T7). */
const STAFF_ROLES = ['SUPER_ADMIN', 'DIRECTOR', 'ADMIN'];
/** Bir imtihonga biriktiriladigan sinflar chegarasi — backend bilan bir xil. */
const MAX_EXAM_CLASSES = 40;

interface ClassOpt { id: string; name: string }

interface Exam {
  id: string;
  title: string;
  date: string;
  maxScore: number;
  subjectId: string | null;
  classId: string | null;
  teacherId: string | null;
  subject: { id: string; name: string; nameRu: string | null; nameEn: string | null } | null;
  class: { id: string; name: string } | null;
  /** T7 — imtihonning HAQIQIY sinflari. Backend uni doim to'ldiradi (eski bitta
   *  sinfli yozuv ham bitta elementli ro'yxat bo'lib keladi), shuning uchun front
   *  faqat shu maydonga tayanadi; `class`/`classId` eski mijozlar uchun qolgan. */
  classes: ClassOpt[];
  /** T7 — imtihonni O'TKAZUVCHI ustoz (yaratuvchi `createdBy` dan farqli). */
  teacher: { id: string; fullName: string } | null;
  createdBy: { id: string; fullName: string } | null;
  _count: { scores: number };
}

interface ScoreStudent {
  id: string;
  fullName: string;
  photo: string | null;
  score: number | null;
  comment: string | null;
}

/** GET /ranking/exams/:id/scores — sinf tanlanmagan umummaktab imtihonida `needClass` qaytadi. */
interface ScoresResponse {
  exam: { id: string; title: string; date: string; maxScore: number; classId: string | null };
  students: ScoreStudent[];
  classId?: string;
  canEnter?: boolean;
  needClass?: boolean;
  classes?: ClassOpt[];
}

interface SaveScoresResponse { ok: boolean; saved: number; removed: number }

/** Jadvalda tahrirlanayotgan qator: bo'sh `score` — bahoni o'chirish (score: null). */
interface ScoreRow { score: number | string | null; comment: string }

/**
 * Test imtihonlar (haftalik) — imtihonlar ro'yxati/CRUD va har bir imtihon uchun
 * o'quvchilarga ball kiritish. Ball kiritish huquqini BACKEND hal qiladi (`canEnter`).
 */
export default function TestExams() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role ?? '');
  const canListStaff = STAFF_ROLES.includes(user?.role ?? '');

  // Ro'yxat / tarix (T8) — bitta sahifada ikki ko'rinish. Alohida marshrut ochilmadi:
  // tarix aynan shu ro'yxatning boshqa kesimi, filtrlari ham bir xil.
  const [mode, setMode] = useState('list');

  // ─── Filtrlar ─────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [classF, setClassF] = useState<string | null>(null);
  const [subjectF, setSubjectF] = useState<string | null>(null);
  const [teacherF, setTeacherF] = useState<string | null>(null);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [ungradedF, setUngradedF] = useState<string | null>(null);
  const [sort, setSort] = useState('date:desc');

  // ─── Modallar ─────────────────────────────────────────────────
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<Exam | null>(null);
  const [scoreExam, setScoreExam] = useState<Exam | null>(null);
  const [scoreClassId, setScoreClassId] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, ScoreRow>>({});
  // Ball kiritish jadvalidagi qidiruv — FAQAT ko'rinishga ta'sir qiladi (`rows` kesilmaydi)
  const [studentSearch, setStudentSearch] = useState('');

  const errMsg = (e: unknown) =>
    (e as { response?: { data?: { error?: string } } })?.response?.data?.error || t('examsPage.saveError');
  const showErr = (e: unknown) => notifications.show({ color: 'red', message: errMsg(e) });

  const { data: classes } = useClasses();
  const { data: subjects } = useSubjects();

  // O'tkazuvchi ustozlar ro'yxati (T7). `/staff?roles=` — `role=` faqat BITTA rolni
  // beradi, bizga sinf rahbarlari ham kerak. O'qituvchi bu endpointga kira olmaydi,
  // shuning uchun so'rov faqat STAFF rollarida yuboriladi.
  const { data: staff } = useQuery({
    queryKey: ['exam-teachers', user?.id],
    queryFn: async () =>
      (await api.get<{ id: string; fullName: string }[]>('/staff', {
        params: { roles: 'TEACHER,HOMEROOM_TEACHER', limit: 500 },
      })).data,
    enabled: canListStaff,
    staleTime: 60_000,
  });
  const teacherData = useMemo(
    () => (staff ?? []).map((s) => ({ value: s.id, label: s.fullName })),
    [staff],
  );
  // Filtr ro'yxati: administratsiya — barcha ustozlar + "biriktirilmagan";
  // o'qituvchi ro'yxatni ko'ra olmagani uchun unga faqat "mening imtihonlarim"
  // (backend `teacherId=me` ni tokendan oladi).
  const teacherFilterOptions = useMemo(
    () => (canListStaff
      ? [{ value: 'none', label: t('examsPage.noTeacher') }, ...teacherData]
      : [{ value: 'me', label: t('examsPage.myExams') }]),
    [canListStaff, teacherData, t],
  );

  // Lokal sana → YYYY-MM-DD. toISOString() EMAS: UTC+5 da kun chegarasi siljib ketardi.
  const iso = (d: Date | null) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

  /* Filtrlar BITTA joyda: jadval ham, eksport ham AYNI obyektni oladi. */
  const filtrlar = {
    q: qDeb || undefined,
    classId: classF || undefined,
    subjectId: subjectF || undefined,
    teacherId: teacherF || undefined,
    from: iso(range[0]),
    to: iso(range[1]),
    ungraded: ungradedF || undefined,
    sort,
  };
  const { items: exams, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Exam>('exams', '/ranking/exams', filtrlar, 20, mode === 'list');

  // ─── Imtihon formasi (manager) ────────────────────────────────
  const form = useForm({
    initialValues: {
      title: '',
      subjectId: '',
      classIds: [] as string[],
      teacherId: '',
      date: new Date() as Date | null,
      maxScore: 100 as number | string,
    },
    validate: {
      title: (v) => (!String(v ?? '').trim() ? t('examsPage.titleRequired') : null),
      classIds: (v) => (v.length > MAX_EXAM_CLASSES ? t('examsPage.tooManyClasses', { max: MAX_EXAM_CLASSES }) : null),
      maxScore: (v) => {
        const n = Number(v);
        return !Number.isInteger(n) || n < 1 || n > 1000 ? t('examsPage.maxScoreRange') : null;
      },
    },
  });

  const openCreate = () => {
    setEditing(null);
    form.setValues({ title: '', subjectId: '', classIds: [], teacherId: '', date: new Date(), maxScore: 100 });
    form.clearErrors();
    open();
  };

  const openEdit = (e: Exam) => {
    setEditing(e);
    form.setValues({
      title: e.title,
      subjectId: e.subjectId ?? '',
      // `classes` — backend shakllantirgan haqiqiy ro'yxat (eski `classId` ham shu yerda)
      classIds: e.classes.map((c) => c.id),
      teacherId: e.teacherId ?? '',
      date: e.date ? new Date(e.date) : null,
      maxScore: e.maxScore,
    });
    form.clearErrors();
    open();
  };

  /** Imtihon o'zgargach ro'yxat ham, TARIX ham eskiradi (T8) — ikkalasi birga tozalanadi. */
  const invalidateExams = () => {
    qc.invalidateQueries({ queryKey: ['exams'] });
    qc.invalidateQueries({ queryKey: ['exam-history'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = {
        title: String(v.title).trim(),
        subjectId: v.subjectId || null,
        // T7: sinflar ro'yxati. Bo'sh massiv = umummaktab imtihoni.
        // Eski `classId` ni backend o'zi to'ldiradi (ro'yxatning birinchisi).
        classIds: v.classIds,
        teacherId: v.teacherId || null,
        // Sana Toshkent KUNI sifatida yuboriladi: `toISOString()` bo'lsa UTC+5 da
        // kechqurun yaratilgan imtihon keyingi kunga o'tib ketardi.
        date: iso(v.date),
        maxScore: Number(v.maxScore),
      };
      return editing ? api.patch(`/ranking/exams/${editing.id}`, body) : api.post('/ranking/exams', body);
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('examsPage.examSaved') });
      invalidateExams();
      setEditing(null);
      form.reset();
      close();
    },
    onError: showErr,
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ranking/exams/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('examsPage.examDeleted') });
      invalidateExams();
    },
    onError: showErr,
  });

  // ─── Ballar modali ────────────────────────────────────────────
  const openScores = (e: Exam) => {
    setScoreExam(e);
    setScoreClassId(null); // imtihonda sinf bo'lsa — backend o'zi oladi
    setRows({});
    setStudentSearch('');
  };
  const closeScores = () => {
    setScoreExam(null);
    setScoreClassId(null);
    setRows({});
    setStudentSearch('');
  };

  const { data: scores, isLoading: scoresLoading } = useQuery({
    queryKey: ['exam-scores', scoreExam?.id, scoreClassId],
    queryFn: async () =>
      (await api.get<ScoresResponse>(`/ranking/exams/${scoreExam!.id}/scores`, {
        params: { classId: scoreClassId || undefined },
      })).data,
    enabled: !!scoreExam?.id,
  });

  // Server javobidan lokal tahrir holatini urug'lantirish (javob o'zgarganda qayta).
  useEffect(() => {
    if (!scores) return;
    const init: Record<string, ScoreRow> = {};
    scores.students.forEach((s) => {
      init[s.id] = { score: s.score ?? '', comment: s.comment ?? '' };
    });
    setRows(init);
  }, [scores]);

  const canEnter = scores?.canEnter === true;
  const needClass = scores?.needClass === true;

  const scoresMut = useMutation({
    mutationFn: async (v: { examId: string; scores: { studentId: string; score: number | null; comment?: string }[] }) =>
      (await api.post<SaveScoresResponse>(`/ranking/exams/${v.examId}/scores`, { scores: v.scores })).data,
    onSuccess: (data) => {
      notifications.show({ color: 'green', message: t('examsPage.scoresSaved', { saved: data.saved }) });
      invalidateExams();
      qc.invalidateQueries({ queryKey: ['exam-scores'] });
      qc.invalidateQueries({ queryKey: ['exam-audit'] });
      qc.invalidateQueries({ queryKey: ['ranking'] });
      closeScores();
    },
    onError: showErr,
  });

  const submitScores = () => {
    if (!scoreExam || !scores) return;
    const payload = scores.students.map((s) => {
      const row = rows[s.id];
      const raw = row?.score;
      const filled = raw !== '' && raw !== null && raw !== undefined;
      const comment = (row?.comment ?? '').trim();
      return {
        studentId: s.id,
        score: filled ? Number(raw) : null,
        comment: comment || undefined,
      };
    });
    if (!payload.length) return;
    scoresMut.mutate({ examId: scoreExam.id, scores: payload });
  };

  const setRow = (studentId: string, patch: Partial<ScoreRow>) =>
    setRows((r) => ({
      ...r,
      [studentId]: { score: r[studentId]?.score ?? '', comment: r[studentId]?.comment ?? '', ...patch },
    }));

  const classData = (classes ?? []).map((c) => ({ value: c.id, label: c.name }));
  const subjectData = (subjects ?? []).map((s) => ({ value: s.id, label: s.name }));

  // Modal jadvalidagi ko'rinadigan o'quvchilar. MUHIM: saqlash to'liq `scores.students`
  // bo'yicha ketadi — bu filtr faqat renderni qisqartiradi.
  const visibleStudents = useMemo(() => {
    const list = scores?.students ?? [];
    const q = studentSearch.trim().toLowerCase();
    return q ? list.filter((s) => s.fullName.toLowerCase().includes(q)) : list;
  }, [scores, studentSearch]);

  /** Jadvaldagi sinflar katakchasi: 2 tadan ko'pi "+N" bo'lib yig'iladi (T7). */
  const classCell = (list: ClassOpt[]) => {
    if (!list.length) return <Text fz="sm" c="dimmed">{t('examsPage.allSchool')}</Text>;
    const shown = list.slice(0, 2);
    return (
      <Group gap={4} wrap="nowrap">
        {shown.map((c) => <Badge key={c.id} variant="light" radius="sm">{c.name}</Badge>)}
        {list.length > shown.length && (
          <Tooltip withArrow label={list.map((c) => c.name).join(', ')}>
            <Badge variant="default" radius="sm">+{list.length - shown.length}</Badge>
          </Tooltip>
        )}
      </Group>
    );
  };

  return (
    <>
      <PageHeader
        title={t('pages.exams.title')}
        action={
          isManager && mode === 'list' && (
            <Button leftSection={<IconPlus size={18} />} onClick={openCreate}>
              {t('examsPage.add')}
            </Button>
          )
        }
      />

      {/* Ro'yxat / tarix (T8) — tarixda natijalar va ball kiritish auditi ko'rinadi */}
      <SegmentedControl
        value={mode}
        onChange={setMode}
        mb="md"
        data={[
          { value: 'list', label: t('examsPage.tabList') },
          { value: 'history', label: t('examsPage.tabHistory') },
        ]}
      />

      {mode === 'history' ? (
        <ExamHistory
          classOptions={classData}
          subjectOptions={subjectData}
          teacherOptions={teacherFilterOptions}
        />
      ) : (
      <>
      <TableToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('examsPage.searchPlaceholder')}
        sort={sort}
        onSort={setSort}
        sortOptions={[
          { value: 'date:desc', label: t('examsPage.sortDateDesc') },
          { value: 'date:asc', label: t('examsPage.sortDateAsc') },
        ]}
      >
        <Select
          placeholder={t('examsPage.allClasses')}
          data={classData}
          value={classF || null}
          onChange={setClassF}
          clearable
          searchable
          w={170}
        />
        <Select
          placeholder={t('examsPage.allSubjects')}
          data={subjectData}
          value={subjectF || null}
          onChange={setSubjectF}
          clearable
          searchable
          w={170}
        />
        {/* T7 — o'tkazuvchi ustoz bo'yicha filtr */}
        <Select
          placeholder={t('examsPage.allTeachers')}
          data={teacherFilterOptions}
          value={teacherF}
          onChange={setTeacherF}
          clearable
          searchable
          w={180}
        />
        <DatePickerInput
          type="range"
          placeholder={t('examsPage.dateRange')}
          value={range}
          onChange={setRange}
          valueFormat="DD.MM.YYYY"
          clearable
          w={190}
        />
        <Select
          placeholder={t('examsPage.allExams')}
          data={[{ value: '1', label: t('examsPage.ungraded') }]}
          value={ungradedF || null}
          onChange={setUngradedF}
          clearable
          w={190}
        />
        <TableExport<Exam>
          endpoint="/ranking/exams"
          params={filtrlar}
          filename={t('nav.exams')}
          ustunlar={[
            { key: 'title', label: t('examsPage.examTitle') },
            { key: 'date', label: t('att.date') },
            { key: 'maxScore', label: t('examsPage.maxScore') },
          ]}
          sanaMaydoni={(r) => r.date}
        />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconChecklist} p={0} pos="relative">
        {isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : !exams.length ? (
          <EmptyState
            icon={IconChecklist}
            title={t('examsPage.noExams')}
            description={t('common.noMatch')}
          />
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('examsPage.examTitle')}</Table.Th>
                  <Table.Th>{t('examsPage.subject')}</Table.Th>
                  <Table.Th>{t('examsPage.classes')}</Table.Th>
                  <Table.Th>{t('examsPage.teacher')}</Table.Th>
                  <Table.Th>{t('examsPage.date')}</Table.Th>
                  <Table.Th>{t('examsPage.maxScore')}</Table.Th>
                  <Table.Th>{t('examsPage.scoresCount')}</Table.Th>
                  <Table.Th>{t('common.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {exams.map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td fw={500}>{e.title}</Table.Td>
                    <Table.Td>{e.subject?.name ?? '—'}</Table.Td>
                    <Table.Td>{classCell(e.classes)}</Table.Td>
                    <Table.Td>{e.teacher?.fullName ?? '—'}</Table.Td>
                    <Table.Td>{formatDate(e.date)}</Table.Td>
                    <Table.Td>{e.maxScore}</Table.Td>
                    <Table.Td><Badge variant="light">{e._count.scores}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Button size="xs" variant="light" onClick={() => openScores(e)}>
                          {t('examsPage.enterScores')}
                        </Button>
                        {isManager && (
                          <>
                            <ActionIcon variant="subtle" onClick={() => openEdit(e)}>
                              <IconPencil size={16} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => confirmDelete(e.title, () => delMut.mutate(e.id))}
                             loading={delMut.isPending}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />
      </>
      )}

      {/* Imtihon yaratish / tahrirlash — faqat manager */}
      <Modal
        opened={opened && isManager}
        onClose={close}
        title={editing ? t('examsPage.edit') : t('examsPage.add')}
        centered
      >
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput
              label={t('examsPage.examTitle')}
              placeholder={t('examsPage.titlePlaceholder')}
              required
              {...form.getInputProps('title')}
            />
            <Select
              label={t('examsPage.subject')}
              placeholder={t('common.selectPlaceholder')}
              data={subjectData}
              clearable
              searchable
              {...form.getInputProps('subjectId')}
            />
            {/* T7 — bitta imtihon bir nechta sinfda o'tkazilishi mumkin.
                Bo'sh qoldirilsa: umummaktab imtihoni (eski xatti-harakat). */}
            <MultiSelect
              label={t('examsPage.classes')}
              placeholder={form.values.classIds.length ? undefined : t('examsPage.selectClasses')}
              description={t('examsPage.classesHint')}
              data={classData}
              clearable
              searchable
              hidePickedOptions
              {...form.getInputProps('classIds')}
            />
            {/* T7 — imtihonni O'TKAZUVCHI ustoz (yaratuvchidan farqli). */}
            <Select
              label={t('examsPage.teacher')}
              placeholder={t('examsPage.noTeacher')}
              data={teacherData}
              clearable
              searchable
              {...form.getInputProps('teacherId')}
            />
            <DateInput label={t('examsPage.date')} valueFormat="DD.MM.YYYY" clearable {...form.getInputProps('date')} />
            <NumberInput label={t('examsPage.maxScore')} min={1} max={1000} {...form.getInputProps('maxScore')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saveMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Ballar kiritish */}
      <Modal
        opened={!!scoreExam}
        onClose={closeScores}
        size="lg"
        centered
        title={
          <div>
            <Text fw={700}>{t('examsPage.scoresTitle')}</Text>
            {scoreExam ? <Text fz="xs" c="dimmed">{scoreExam.title}</Text> : null}
          </div>
        }
      >
        {scoresLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !scores ? null : needClass ? (
          <Select
            label={t('fields.class')}
            placeholder={t('examsPage.selectClassForScores')}
            data={(scores.classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={scoreClassId}
            onChange={setScoreClassId}
            searchable
          />
        ) : !scores.students.length ? (
          <Text c="dimmed">{t('examsPage.noStudents')}</Text>
        ) : (
          <Stack gap="sm">
            {!canEnter ? (
              <Text c="dimmed" fz="sm">{t('examsPage.noPermission')}</Text>
            ) : (
              <Text c="dimmed" fz="sm">{t('examsPage.scoreRange', { max: scores.exam.maxScore })}</Text>
            )}
            <TextInput
              placeholder={t('examsPage.searchStudent')}
              leftSection={<IconSearch size={16} />}
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.currentTarget.value)}
              w={260}
            />
            <Table.ScrollContainer minWidth={520}>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('examsPage.student')}</Table.Th>
                    <Table.Th w={130}>{t('examsPage.score')}</Table.Th>
                    <Table.Th>{t('examsPage.comment')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {!visibleStudents.length && (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text c="dimmed" ta="center" py="md">{t('common.noMatch')}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {visibleStudents.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.fullName}</Table.Td>
                      <Table.Td>
                        <NumberInput
                          min={0}
                          max={scores.exam.maxScore}
                          w={110}
                          disabled={!canEnter}
                          value={rows[s.id]?.score ?? ''}
                          onChange={(v) => setRow(s.id, { score: v })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          disabled={!canEnter}
                          value={rows[s.id]?.comment ?? ''}
                          onChange={(ev) => setRow(s.id, { comment: ev.currentTarget.value })}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeScores}>{t('common.cancel')}</Button>
              {canEnter && (
                <Button onClick={submitScores} loading={scoresMut.isPending}>{t('common.save')}</Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
